import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { brand } from '@iheart/shared';
import { MotionButton, GradientText } from '@iheart/ui';
import { ApiError, useAuth } from '../lib/auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { PublicFooter, PublicNav } from '../components/PublicFooter';

export function LoginRoute() {
  useDocumentTitle('Sign in');
  const { login, completeMfa, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MFA challenge step: set after a correct password when the account has MFA on.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  // If user was redirected from a protected page, show a session expired hint
  const wasRedirected = from !== '/';

  // SSO error from redirect
  const ssoError = searchParams.get('error');
  const ssoProvider = searchParams.get('provider');

  // If we boot already-signed-in (silent refresh succeeded), bounce home.
  useEffect(() => {
    if (status === 'authenticated') navigate(from, { replace: true });
  }, [status, from, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (res.mfaRequired && res.mfaToken) {
        setMfaToken(res.mfaToken);
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setError(err.status === 401 ? 'Invalid email or password.' : body?.message ?? 'Sign in failed.');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setSubmitting(true);
    try {
      await completeMfa(mfaToken, mfaCode);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 401 ? 'That code is incorrect or expired. Try again.' : 'Verification failed.');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12 pt-20">
      <PublicNav />
      {/* Subtle gradient backdrop */}
      <div
        className="pointer-events-none absolute -left-40 -top-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 -right-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />

      <div className="relative w-full max-w-sm rounded-2xl border border-white/60 bg-white/80 p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="iHeart CNTRL" className="size-16" />
          <h1 className="text-2xl font-semibold">
            Welcome to <GradientText>iHeart CNTRL</GradientText>
          </h1>
          <p className="text-sm text-neutral-500">Social scheduling. Total control.</p>
        </div>

        {wasRedirected && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs text-amber-700">
            Your session expired. Please sign in again.
          </div>
        )}

        {mfaToken ? (
          <form className="flex flex-col gap-3" onSubmit={onMfaSubmit}>
            <div>
              <label htmlFor="mfa-code" className="mb-1 block text-sm font-medium text-neutral-700">
                Authentication code
              </label>
              <p className="mb-2 text-xs text-neutral-500">
                Enter the 6-digit code from your authenticator app, or a backup code.
              </p>
              <input
                id="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="123456"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-center text-lg tracking-widest outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-100"
              />
            </div>
            {error && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
            <MotionButton type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
              {submitting ? 'Verifying…' : 'Verify & sign in'}
            </MotionButton>
            <button
              type="button"
              onClick={() => { setMfaToken(null); setMfaCode(''); setError(null); }}
              className="text-center text-xs text-neutral-500 hover:text-neutral-700"
            >
              ← Back to sign in
            </button>
          </form>
        ) : (
        <>
        <form className="flex flex-col gap-3" onSubmit={onSubmit}>
          <div>
            <label htmlFor="login-email" className="mb-1 block text-sm font-medium text-neutral-700">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@iheartdigital.co.za"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="mb-1 block text-sm font-medium text-neutral-700">
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-border bg-white px-3 py-2.5 pr-10 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-neutral-400 transition-colors hover:text-neutral-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Link
              to="/auth/forgot-password"
              className="text-xs font-medium text-pink-600 transition-colors hover:text-pink-700"
            >
              Forgot password?
            </Link>
          </div>

          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <MotionButton type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </MotionButton>
        </form>

        {/* SSO error message */}
        {ssoError && (
          <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-center text-xs text-red-700">
            {ssoError === 'no_account'
              ? 'No account found for this email. Please register for early access first.'
              : ssoError === 'sso_state_mismatch'
                ? 'SSO session expired. Please try again.'
                : `Sign in with ${ssoProvider || 'SSO'} failed. Please try again.`}
          </div>
        )}

        {/* SSO divider */}
        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-neutral-200" />
          <span className="text-xs font-medium text-neutral-400">or continue with</span>
          <div className="h-px flex-1 bg-neutral-200" />
        </div>

        {/* SSO buttons */}
        <div className="flex flex-col gap-2.5">
          <a
            href="/api/auth/google"
            className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 hover:border-neutral-300"
          >
            <svg className="size-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </a>
          <a
            href="/api/auth/microsoft"
            className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 hover:border-neutral-300"
          >
            <svg className="size-5" viewBox="0 0 21 21">
              <rect fill="#F25022" x="1" y="1" width="9" height="9"/>
              <rect fill="#7FBA00" x="11" y="1" width="9" height="9"/>
              <rect fill="#00A4EF" x="1" y="11" width="9" height="9"/>
              <rect fill="#FFB900" x="11" y="11" width="9" height="9"/>
            </svg>
            Sign in with Microsoft
          </a>
        </div>
        </>
        )}

        <p className="mt-5 text-center text-xs text-neutral-400">
          Account creation is invite-only.
          <br />
          Ask a workspace admin for an invite link.
        </p>
      </div>

      <PublicFooter />
    </div>
  );
}
